import { type Option } from '@/components/Form';
import { useState } from 'react';
import {
  type FilterFormState,
  type OrganizationSearchCondition,
  type MultiSelectFieldKey,
  type OrganizationSort,
} from '../types';
import { SORT_DIRECTION } from '@/constants/sort';

const INITIAL_FORM_STATE: FilterFormState = {
  query: '',
  types: [],
  statuses: [],
  countries: [],
  countryTags: [],
  sort: { field: 'NAME', direction: SORT_DIRECTION.ASC },
};

export const useOrganizationFilters = () => {
  const [formState, setFormState] =
    useState<FilterFormState>(INITIAL_FORM_STATE);
  const [appliedFilters, setAppliedFilters] =
    useState<FilterFormState>(INITIAL_FORM_STATE);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const buildAppliedFilters = (state: FilterFormState): FilterFormState => ({
    query: state.query.trim(),
    types: [...state.types],
    statuses: [...state.statuses],
    countries: [...state.countries],
    countryTags: [...state.countryTags],
    sort: { ...state.sort },
  });

  const parsedCondition: OrganizationSearchCondition = {
    query: appliedFilters.query.trim() || undefined,
    types:
      appliedFilters.types.length > 0
        ? appliedFilters.types.map(({ value }) => value)
        : undefined,
    statuses:
      appliedFilters.statuses.length > 0
        ? appliedFilters.statuses.map(({ value }) => value)
        : undefined,
    countries:
      appliedFilters.countries.length > 0
        ? appliedFilters.countries.map(({ value }) => value)
        : undefined,
    countryTags:
      appliedFilters.countryTags.length > 0
        ? appliedFilters.countryTags.map(({ value }) => value)
        : undefined,
    sort: appliedFilters.sort,
  };
  const condition: OrganizationSearchCondition = parsedCondition;

  const handleSelectChange =
    (field: MultiSelectFieldKey) => (options: readonly Option[] | null) => {
      const nextOptions = options ? [...options] : [];

      const updater = (prev: FilterFormState) => ({
        ...prev,
        [field]: nextOptions,
      });

      setFormState(updater);
      setAppliedFilters(updater);
    };

  const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setAppliedFilters(buildAppliedFilters(formState));
  };

  const handleReset = () => {
    setFormState(INITIAL_FORM_STATE);
    setAppliedFilters(INITIAL_FORM_STATE);
  };

  const handleSortChange = (sort: OrganizationSort) => {
    setFormState((prev) => ({ ...prev, sort }));
    setAppliedFilters((prev) => ({ ...prev, sort }));
  };

  return {
    formState,
    appliedFilters,
    isFilterExpanded,
    condition,
    setFormState,
    setIsFilterExpanded,
    handleSubmit,
    handleSelectChange,
    handleReset,
    handleSortChange,
  };
};
